import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";  // CardHeader, CardTitleを削除
import { Badge } from "./ui/badge";
// Sliderを削除
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  Volume2, 
  VolumeX,
  Eye,
  EyeOff,
  Settings,
  Maximize,
  RotateCcw,
  Zap,
  Mic,
  MicOff
} from "lucide-react";

export default function XRGuideSimulator() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [arOverlayVisible, setArOverlayVisible] = useState(true);
  const [gestureMode, setGestureMode] = useState(false);
  const [viewMode, setViewMode] = useState("surgeon"); // surgeon, scrub, circulator
  const [opacity, setOpacity] = useState(80);  // Sliderの代わりに数値で管理
  const [isListening, setIsListening] = useState(false);
  
  const videoRef = useRef(null);

  // サンプル手術手順データ
  const procedure = {
    name: "幽門側胃切除術",
    steps: [
      {
        id: 1,
        title: "患者体位・麻酔導入",
        description: "仰臥位、全身麻酔下での準備",
        duration: 30,
        arElements: [
          {
            id: "patient-position",
            type: "3d-model",
            position: { x: 50, y: 60 },
            content: "患者体位ガイド",
            priority: 1,
            visibility: { surgeon: true, scrub: false, circulator: true }
          },
          {
            id: "anesthesia-monitor",
            type: "vital-display",
            position: { x: 80, y: 20 },
            content: "バイタル監視",
            priority: 2,
            visibility: { surgeon: true, scrub: false, circulator: true }
          }
        ],
        instruments: ["基本セット"],
        scrubInstructions: "基本セットを準備してください",
        circulatorInstructions: "体位確認と麻酔器チェック",
        voiceScript: "ステップ1：患者体位と麻酔導入を開始します。仰臥位を確認し、麻酔器の準備をお願いします。"
      },
      {
        id: 2,
        title: "清潔野展開",
        description: "手術野の消毒と清潔野の確保",
        duration: 15,
        arElements: [
          {
            id: "sterile-field",
            type: "area-highlight",
            position: { x: 40, y: 40 },
            content: "清潔野範囲",
            priority: 1,
            visibility: { surgeon: true, scrub: true, circulator: false }
          },
          {
            id: "instrument-table",
            type: "table-guide",
            position: { x: 20, y: 70 },
            content: "器械台配置",
            priority: 2,
            visibility: { surgeon: false, scrub: true, circulator: true }
          }
        ],
        instruments: ["消毒セット", "ドレープ", "開創器"],
        scrubInstructions: "ドレープ展開後、開創器を準備してください",
        circulatorInstructions: "清潔野の範囲を確認し、電気メスを接続",
        voiceScript: "ステップ2：清潔野展開を行います。ドレープを展開し、開創器の準備をお願いします。"
      },
      {
        id: 3,
        title: "腹部切開・開腹",
        description: "正中切開による腹腔内へのアプローチ",
        duration: 20,
        arElements: [
          {
            id: "incision-line",
            type: "line-guide",
            position: { x: 50, y: 45 },
            content: "切開ライン",
            priority: 1,
            visibility: { surgeon: true, scrub: false, circulator: false }
          },
          {
            id: "cautery-ready",
            type: "instrument-highlight",
            position: { x: 70, y: 30 },
            content: "電気メス準備",
            priority: 2,
            visibility: { surgeon: true, scrub: true, circulator: false }
          }
        ],
        instruments: ["メス", "電気メス", "止血鉗子"],
        scrubInstructions: "メスを渡し、電気メスを準備",
        circulatorInstructions: "電気メス出力確認",
        voiceScript: "ステップ3：腹部切開を開始します。メスの準備と電気メスの出力確認をお願いします。"
      },
      {
        id: 4,
        title: "胃の遊離・切除",
        description: "胃の血管処理と切除ライン決定",
        duration: 90,
        arElements: [
          {
            id: "vessel-anatomy",
            type: "anatomy-overlay",
            position: { x: 45, y: 50 },
            content: "血管解剖",
            priority: 1,
            visibility: { surgeon: true, scrub: false, circulator: false }
          },
          {
            id: "stapler-ready",
            type: "instrument-highlight",
            position: { x: 25, y: 60 },
            content: "ステープラー準備",
            priority: 2,
            visibility: { surgeon: true, scrub: true, circulator: false }
          }
        ],
        instruments: ["腸鉗子", "ステープラー", "エネルギーデバイス"],
        scrubInstructions: "ステープラーを準備し、腸鉗子を待機",
        circulatorInstructions: "追加器械を準備",
        voiceScript: "ステップ4：胃の遊離と切除を行います。ステープラーと腸鉗子の準備をお願いします。"
      }
    ]
  };

  const currentStepData = procedure.steps[currentStep];

  useEffect(() => {
    if (isPlaying) {
      const timer = setInterval(() => {
        if (currentStep < procedure.steps.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          setIsPlaying(false);
        }
      }, 3000); // 3秒ごとに次のステップ

      return () => clearInterval(timer);
    }
  }, [isPlaying, currentStep]);

  const speakText = (text) => {
    if (voiceEnabled && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'ja-JP';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  };

  const startVoiceRecognition = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.lang = 'ja-JP';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript.toLowerCase();
        handleVoiceCommand(transcript);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    }
  };

  const handleVoiceCommand = (command) => {
    if (command.includes('次') || command.includes('つぎ')) {
      nextStep();
    } else if (command.includes('前') || command.includes('まえ')) {
      prevStep();
    } else if (command.includes('再生') || command.includes('開始')) {
      setIsPlaying(true);
    } else if (command.includes('停止') || command.includes('一時停止')) {
      setIsPlaying(false);
    } else if (command.includes('読み上げ') || command.includes('音声')) {
      speakText(currentStepData.voiceScript);
    }
  };

  const nextStep = () => {
    if (currentStep < procedure.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getCurrentInstructions = () => {
    switch (viewMode) {
      case "surgeon":
        return currentStepData.description;
      case "scrub":
        return currentStepData.scrubInstructions;
      case "circulator":
        return currentStepData.circulatorInstructions;
      default:
        return currentStepData.description;
    }
  };

  const getVisibleARElements = () => {
    return currentStepData.arElements.filter(element => 
      element.visibility[viewMode] && arOverlayVisible
    );
  };

  const renderAROverlay = () => (
    <div 
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: opacity / 100 }}
    >
      {getVisibleARElements().map(element => (
        <div
          key={element.id}
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${element.position.x}%`,
            top: `${element.position.y}%`,
          }}
        >
          <div className={`
            bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg text-sm font-medium
            ${element.priority === 1 ? 'ring-2 ring-yellow-400' : ''}
            animate-pulse
          `}>
            <div className="flex items-center gap-2">
              {element.type === "3d-model" && "🏥"}
              {element.type === "vital-display" && "📊"}
              {element.type === "area-highlight" && "🎯"}
              {element.type === "table-guide" && "📋"}
              {element.type === "line-guide" && "✂️"}
              {element.type === "instrument-highlight" && "🔧"}
              {element.type === "anatomy-overlay" && "🫀"}
              {element.content}
            </div>
            {element.priority === 1 && (
              <div className="text-xs text-yellow-300 mt-1">重要</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">XR手術ガイド シミュレーター</h1>
        <div className="flex gap-2">
          <Badge variant="outline" className="flex items-center gap-1">
            <Zap className="w-3 h-3" />
            {procedure.name}
          </Badge>
          <Badge variant="secondary">
            Step {currentStep + 1}/{procedure.steps.length}
          </Badge>
        </div>
      </div>

      {/* 視点切り替え */}
      <div className="flex gap-2">
        <Button 
          variant={viewMode === "surgeon" ? "default" : "outline"}
          onClick={() => setViewMode("surgeon")}
          size="sm"
        >
          👨‍⚕️ 術者視点
        </Button>
        <Button 
          variant={viewMode === "scrub" ? "default" : "outline"}
          onClick={() => setViewMode("scrub")}
          size="sm"
        >
          🧤 器械出し
        </Button>
        <Button 
          variant={viewMode === "circulator" ? "default" : "outline"}
          onClick={() => setViewMode("circulator")}
          size="sm"
        >
          🔄 外回り
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* メインビュー（XRシミュレーション） */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">XR表示シミュレーション</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setArOverlayVisible(!arOverlayVisible)}
                  >
                    {arOverlayVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm">
                    <Maximize className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
                {/* 背景画像（手術室のシミュレーション） */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900">
                  <div className="absolute inset-0 opacity-30">
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-white text-6xl">🏥</div>
                    </div>
                  </div>
                </div>

                {/* AR オーバーレイ */}
                {renderAROverlay()}

                {/* ジェスチャーガイド */}
                {gestureMode && (
                  <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white p-3 rounded">
                    <div className="text-sm font-medium mb-2">ジェスチャーコマンド:</div>
                    <div className="text-xs space-y-1">
                      <div>👆 上スワイプ: 次のステップ</div>
                      <div>👇 下スワイプ: 前のステップ</div>
                      <div>👋 手のひら: 一時停止</div>
                      <div>✊ グー: 再生</div>
                    </div>
                  </div>
                )}

                {/* 音声認識インジケーター */}
                {isListening && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white p-2 rounded-full animate-pulse">
                    <Mic className="w-4 h-4" />
                  </div>
                )}
              </div>

              {/* AR設定 */}
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">ARオーバーレイ透明度</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="10"
                      value={opacity}
                      onChange={(e) => setOpacity(parseInt(e.target.value))}
                      className="w-32"
                    />
                    <span className="text-sm text-gray-600 w-8">{opacity}%</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGestureMode(!gestureMode)}
                  >
                    ✋ ジェスチャー: {gestureMode ? "ON" : "OFF"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startVoiceRecognition}
                    disabled={isListening}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    音声コマンド
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* コントロールパネル */}
        <div className="space-y-6">
          {/* 再生コントロール */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4">再生コントロール</h2>
              <div className="space-y-4">
                <div className="flex justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={prevStep}>
                    <SkipBack className="w-4 h-4" />
                  </Button>
                  <Button 
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="w-16"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={nextStep}>
                    <SkipForward className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>進行状況</span>
                    <span>{currentStep + 1} / {procedure.steps.length}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((currentStep + 1) / procedure.steps.length) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm">音声ガイド</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setVoiceEnabled(!voiceEnabled)}
                  >
                    {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </Button>
                </div>

                <Button 
                  className="w-full"
                  onClick={() => speakText(currentStepData.voiceScript)}
                  disabled={!voiceEnabled}
                >
                  現在のステップを読み上げ
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 現在のステップ情報 */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4">
                Step {currentStep + 1}: {currentStepData.title}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm mb-2">指示内容:</h4>
                  <p className="text-sm text-gray-700">{getCurrentInstructions()}</p>
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-2">必要器械:</h4>
                  <div className="flex flex-wrap gap-1">
                    {currentStepData.instruments.map(instrument => (
                      <Badge key={instrument} variant="outline" className="text-xs">
                        {instrument}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-2">予想時間:</h4>
                  <Badge>{currentStepData.duration}分</Badge>
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-2">AR表示要素:</h4>
                  <div className="space-y-1">
                    {getVisibleARElements().map(element => (
                      <div key={element.id} className="text-xs flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          element.priority === 1 ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}></div>
                        {element.content}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ステップ一覧 */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4">手術ステップ一覧</h2>
              <div className="space-y-2">
                {procedure.steps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`p-3 rounded border cursor-pointer transition-colors ${
                      index === currentStep 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setCurrentStep(index)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{step.title}</div>
                        <div className="text-xs text-gray-600">{step.duration}分</div>
                      </div>
                      {index === currentStep && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}